import { Box } from "@rebass/grid";
import { noteModule } from "@rentiohq/shared-frontend/dist/reduxV2/note";
import { INote } from "@rentiohq/shared-frontend/dist/types/notes.types";
import { subDays } from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  Card,
  ESpacings,
  ICardProps,
  Modal,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import utils from "@rentiohq/web-shared/dist/utils";
import React from "react";
import { useDispatch } from "react-redux";
import * as t from "../../services/translationService";
import { NoteForm } from "./components";
import * as S from "./InlineNotes.styled";

/** Enough that no candidacy runs past it in practice. */
const ALL_NOTES_LIMIT = 50;

export interface IProps {
  customPath: string;
  cardProps?: ICardProps;
  customContentBefore?: React.ReactNode;
  customContentAfter?: React.ReactNode;
  /**
   * Show every note, newest first, each with its own edit and delete.
   *
   * Opt in, because everywhere else a note is one piece of text about one
   * thing and a list there would be a change nobody asked for. A candidacy is
   * the exception: what an owner was shown is filed beside what the broker
   * wrote, and both have to stay readable (Jonas, 2026-10-06).
   */
  showAllNotes?: boolean;
}

export const InlineNotes = (props: IProps) => {
  const {
    customPath,
    cardProps,
    customContentBefore,
    customContentAfter,
    showAllNotes = false,
  } = props;

  const dispatch = useDispatch();

  const {
    items: notes,
    isFetching,
    refetch,
  } = noteModule.hooks.usePaged({
    shouldRefetch: true,
    query: showAllNotes
      ? {
          page: 1,
          limit: ALL_NOTES_LIMIT,
          // Newest first, said out loud rather than left to the API's own
          // order: the whole point of the list is that the most recent thing
          // said about this person is the first thing read.
          sort: [{ field: "createdAt", method: "DESC" }],
        }
      : {
          page: 1,
          limit: 1,
        },
    customPath,
  });
  const note = notes?.[0];

  const [modalOpen, setModalOpen] = React.useState<boolean>();
  // Which note is being written: a note for the edit of an existing one,
  // undefined with the form open for a new one.
  const [editingNote, setEditingNote] = React.useState<INote | undefined>();
  const [noteToDelete, setNoteToDelete] = React.useState<INote | undefined>();
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleClickNote = () => {
    setEditingNote(showAllNotes ? undefined : note);
    setModalOpen(true);
  };

  const handleClickEditNote = (noteToEdit: INote) => {
    setEditingNote(noteToEdit);
    setModalOpen(true);
  };

  const handleNoteModalClose = () => {
    setModalOpen(false);
    setEditingNote(undefined);
  };

  const handleAddSubmit = async () => {
    handleNoteModalClose();
    refetch();
  };

  const handleConfirmDelete = () => {
    if (!noteToDelete) {
      return;
    }

    const onSettled = () => {
      setIsDeleting(false);
      setNoteToDelete(undefined);
      refetch();
    };

    setIsDeleting(true);
    dispatch(
      noteModule.actions.removeStart.getAction({
        id: noteToDelete.id,
        customPath,
        onSuccess: onSettled,
        onFailure: onSettled,
      }),
    );
  };

  //helpers
  const getNoteSubtitle = (forNote: INote) => {
    const dateModified = forNote.modifiedAt || forNote.createdAt;

    return `${t.noteLastUpdated()}: ${utils.date.diffForHumans(dateModified, {
      to: subDays(new Date(), 7),
      firstWeekAsString: false,
    })}`;
  };

  const getSubtitle = () => {
    if (!note || showAllNotes) {
      return null;
    }

    return getNoteSubtitle(note);
  };

  // Render
  const renderForm = () => (
    <>
      <Box mb={ESpacings.tight}>
        <NoteForm
          key={`note-form-${editingNote?.id ?? "new"}`}
          note={editingNote}
          customPath={customPath}
          onClose={handleNoteModalClose}
          onSubmit={handleAddSubmit}
        />
      </Box>
      <Button onClick={handleNoteModalClose} appearance="link">
        {t.system("cancel")}
      </Button>
    </>
  );

  const renderNoteBody = (forNote: INote, onClick?: () => void) => (
    <S.Wrapper onClick={onClick}>{forNote.content}</S.Wrapper>
  );

  const renderEmpty = () => {
    if (isFetching) {
      return (
        <TextStyle variation="subdued">
          {getLocalizedText("system.loading")}
        </TextStyle>
      );
    }

    return (
      <TextStyle variation="subdued">
        {getLocalizedText("task.note_empty")}
      </TextStyle>
    );
  };

  const renderAllNotes = () => (
    <>
      {modalOpen && !editingNote && renderForm()}
      {!notes?.length && !modalOpen && renderEmpty()}
      {notes?.map(item => {
        if (modalOpen && editingNote?.id === item.id) {
          return <S.NoteItem key={item.id}>{renderForm()}</S.NoteItem>;
        }

        return (
          <S.NoteItem key={item.id}>
            <S.NoteItemHeader>
              <TextStyle variation="subdued" size="small">
                {getNoteSubtitle(item)}
              </TextStyle>
              <S.NoteItemActions>
                <Button
                  onClick={() => handleClickEditNote(item)}
                  appearance="link"
                >
                  {t.noteEdit()}
                </Button>
                <Button onClick={() => setNoteToDelete(item)} appearance="link">
                  {t.system("delete")}
                </Button>
              </S.NoteItemActions>
            </S.NoteItemHeader>
            {renderNoteBody(item, () => handleClickEditNote(item))}
          </S.NoteItem>
        );
      })}
    </>
  );

  const renderContent = () => {
    if (showAllNotes) {
      return renderAllNotes();
    }

    if (modalOpen) {
      return renderForm();
    }

    if (!note) {
      return renderEmpty();
    }

    return renderNoteBody(note, handleClickNote);
  };

  return (
    <>
      {noteToDelete && (
        <Modal
          width="small"
          onClose={() => setNoteToDelete(undefined)}
          heading={getLocalizedText("note.delete.confirm.heading")}
          actions={[
            {
              content: t.system("cancel"),
              appearance: "outline",
              onClick: () => setNoteToDelete(undefined),
            },
            {
              content: t.system("delete"),
              appearance: "primary",
              isDisabled: isDeleting,
              onClick: handleConfirmDelete,
            },
          ]}
        >
          <TextStyle>{getLocalizedText("note.delete.confirm.body")}</TextStyle>
        </Modal>
      )}
      <Card
        heading={t.noteTitle()}
        subHeading={getSubtitle()}
        space={ESpacings.loose}
        actions={[
          {
            content: !note || showAllNotes ? t.noteAdd() : t.noteEdit(),
            onClick: handleClickNote,
            appearance: "link",
          },
        ]}
        {...cardProps}
      >
        {customContentBefore}
        {renderContent()}
        {customContentAfter}
      </Card>
    </>
  );
};
